import i18n from "../../../locales/i18n";
import {
  includeBsadInput,
  mainTemplateColorInput,
  mainListingInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVerticalFooterPrompt,
  getVisualStylePrompt,
  getColorInfo,
  hasRealtorImage,
  promptGenerationFinalParams,
  promptGeneratorInitialParams,
  templateDataType,
  templateInput
} from "../template_sections";

const CELEBRATION_IMAGE_NAME = "celebration_image";

const celebrationImageInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.soldAnnouncement.celebrationImage"),
  type: "image",
  nameInPrompt: CELEBRATION_IMAGE_NAME,
  showInSection: "template_specific",
  description: i18n.t("creativesGenerator.templates.soldAnnouncement.celebrationImageDescription"),
  required: true
};

const backgroundStyleInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.soldAnnouncement.backgroundStyle"),
  type: "text",
  nameInPrompt: "background_style",
  showInSection: "template_specific",
  description: i18n.t("creativesGenerator.templates.soldAnnouncement.backgroundStyleDescription"),
  defaultValue: () =>
    i18n.t("creativesGenerator.templates.soldAnnouncement.backgroundStyleDefaultValue")
};

const soldTextStyleInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.soldAnnouncement.soldTextStyle"),
  type: "text",
  nameInPrompt: "sold_text_style",
  showInSection: "template_specific",
  description: i18n.t("creativesGenerator.templates.soldAnnouncement.soldTextStyleDescription"),
  defaultValue: () =>
    i18n.t("creativesGenerator.templates.soldAnnouncement.soldTextStyleDefaultValue")
};

const layoutInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.soldAnnouncement.layout"),
  type: "text",
  nameInPrompt: "layout",
  showInSection: "template_specific",
  description: i18n.t("creativesGenerator.templates.soldAnnouncement.layoutDescription"),
  defaultValue: () => i18n.t("creativesGenerator.templates.soldAnnouncement.layoutDefaultValue")
};

const headlineTextInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.soldAnnouncement.headlineText"),
  type: "text",
  nameInPrompt: "headline_text",
  showInSection: "template_specific",
  description: i18n.t("creativesGenerator.templates.soldAnnouncement.headlineTextDescription"),
  defaultValue: () =>
    i18n.t("creativesGenerator.templates.soldAnnouncement.headlineTextDefaultValue")
};

const subtitleTextInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.soldAnnouncement.subtitleText"),
  type: "text",
  nameInPrompt: "subtitle_text",
  showInSection: "template_specific",
  description: i18n.t("creativesGenerator.templates.soldAnnouncement.subtitleTextDescription"),
  defaultValue: (params: promptGeneratorInitialParams) => {
    const mainListing = params.selectedListings["main_listing"];
    const city = mainListing?.address?.city;
    return city
      ? i18n.t("creativesGenerator.templates.soldAnnouncement.subtitleTextDefaultValueWithCity", {
          city
        })
      : i18n.t("creativesGenerator.templates.soldAnnouncement.subtitleTextDefaultValue");
  }
};

const hasCelebrationImage = (params: promptGenerationFinalParams): boolean => {
  const value = params.templateFinalInputs.find(
    (input) => input.nameInPrompt === CELEBRATION_IMAGE_NAME
  )?.finalValue;
  return typeof value === "string" && value.trim() !== "";
};

const getTextValue = (params: promptGenerationFinalParams, nameInPrompt: string): string => {
  const value = params.templateFinalInputs.find(
    (input) => input.nameInPrompt === nameInPrompt
  )?.finalValue;
  return typeof value === "string" ? value : "";
};

export const sold_announcement_var_1: templateDataType = {
  templateName: i18n.t("creativesGenerator.templates.soldAnnouncement.templateName"),
  displayImageUrl: "/static/creative-template-display-images/real-estate-sold-announcement-1.jpg",
  description: i18n.t("creativesGenerator.templates.soldAnnouncement.description"),
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    { ...mainListingInput, required: false },
    celebrationImageInput,
    backgroundStyleInput,
    soldTextStyleInput,
    layoutInput,
    headlineTextInput,
    subtitleTextInput,
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    { ...includeRealtorImageInput, defaultValue: () => false },
    realtorLogoTemplateInput
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, mainColorName, lighterColorValue } = getColorInfo(params);
    const backgroundStyle = getTextValue(params, "background_style");
    const soldTextStyle = getTextValue(params, "sold_text_style");
    const layoutChoice = getTextValue(params, "layout");
    const headlineText = getTextValue(params, "headline_text");
    const subtitleText = getTextValue(params, "subtitle_text");
    const includesCelebrationImage = hasCelebrationImage(params);

    return `
{
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "purpose": "Celebratory 'Sold' announcement that does NOT require a property photo",
    "tone": "Professional, celebratory and warm",
    "aspect_ratio": "1:1 (Square)"
  },
  ${getVisualStylePrompt(params)},
  "user_styling_choices": {
    "background_style": "${backgroundStyle}",
    "sold_text_style": "${soldTextStyle}",
    "layout": "${layoutChoice}",
    "instructions": "Treat the three values above as authoritative styling directions provided by the user. Adapt the composition so the background, the rendering of the SOLD/headline text, and the overall layout follow these choices."
  },
  "composition_layers": {
    "background_layer": {
      "instructions": "Render the background according to the user's 'background_style' choice. Use ${mainColorValue} (${mainColorName}) as the dominant accent and ${lighterColorValue} for highlights. ${
      includesCelebrationImage
        ? `The user has also provided a '${CELEBRATION_IMAGE_NAME}' — incorporate it according to the user's 'layout' choice (e.g. as the full background, an inset photo, or one side of a split layout). Use the provided image EXACTLY AS GIVEN; do NOT replace it, redraw it, or invent a different scene. The image may be a property photo, a photo of people, a celebration moment, or anything else — treat its content as-is.`
        : `No celebration image was provided — generate a tasteful graphic background that matches the chosen background_style (no photographic content, no invented houses or people).`
    }"
    },
    "hero_typography_layer": {
      "text": "${headlineText}",
      "position": "Top center or upper-third, following the 'layout' choice",
      "style": "Render the headline according to the user's 'sold_text_style' choice. If the headline contains the word SOLD or נמכר, that word should be the visual focal point. Add a clean drop shadow so the headline separates from the background."
    },
    "subtitle_layer": {
      "text": "${subtitleText}",
      "position": "Just below the headline, secondary visual weight",
      "style": "Clean bold Hebrew text, RTL-aware, contrasting strongly with the background. Smaller than the headline but clearly legible."
    },
    "foreground_effects_layer": {
      "elements": [
        "Subtle floating accents in ${lighterColorValue} (confetti, leaves, sparkles or geometric shapes — pick what best matches the background_style)",
        "Soft light/glow behind the headline for depth"
      ]${getBsadPrompt(params, false)}
    },
    ${
      hasRealtorImage(params)
        ? `${getRealtorImagePrompt(
            params,
            "The realtor cutout should sit cleanly inside the footer area without obscuring the headline."
          )},`
        : ""
    }
    ${getVerticalFooterPrompt(params)}
  },
  "provided_assets": {
    ${
      includesCelebrationImage
        ? `"${CELEBRATION_IMAGE_NAME}": "an image provided by the user — use as-is, do NOT replace or alter the scene",`
        : ""
    }
    ${
      hasRealtorImage(params) ? `"realtor_image": "the realtor image (remove the background)",` : ""
    }
    "realtor_logo": "the realtor logo"
  },
  "important_notes": [
    "This is an RTL (right-to-left) Hebrew design — Hebrew text must be right-aligned or centered as specified",
    "Respect the user's three styling choices (background_style, sold_text_style, layout) above all defaults",
    ${
      includesCelebrationImage
        ? `"The provided celebration image is sacred — keep its content untouched. Do NOT generate a substitute house, do NOT redraw the people, do NOT replace the scene with stock imagery",`
        : `"No celebration image was provided — do NOT invent a property photo or photograph; keep the design graphic and typographic",`
    }
    "The headline is the focal point — make it dominate the composition",
    "Use ${mainColorValue} (${mainColorName}) as the main accent color throughout",
    "Footer must contain the realtor name, phone number, and logo as specified"
  ]
}
`;
  }
};
